import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Upload } from 'lucide-react';
import { SOURCES, FileSource } from './sources';
import SourceCard from './SourceCard';
import YearSelect from './YearSelect';

export type CreaReportModalMode = 'create' | 'add-sources';

interface Props {
  open:               boolean;
  mode?:              CreaReportModalMode;
  /** In mode='add-sources' filtra le SOURCES a quelle con `id` qui dentro. */
  visibleSourceIds?:  string[];
  onClose:            () => void;
  onConfirm:          (data: { titolo: string; anno: number; files: Record<string, File[]> }) => void;
}

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - i);
const LOADING_DELAY_MS = 400;

const CreaReportModal = ({ open, mode = 'create', visibleSourceIds, onClose, onConfirm }: Props) => {
  const [anno, setAnno]       = useState<number>(currentYear);
  const [titolo, setTitolo]   = useState('');
  const [files, setFiles]     = useState<Record<string, File[]>>({});
  const [loading, setLoading] = useState<string | null>(null);

  const isAddMode = mode === 'add-sources';

  const visibleSources = useMemo<FileSource[]>(() => {
    if (!visibleSourceIds) return SOURCES;
    const allow = new Set(visibleSourceIds);
    return SOURCES.filter(s => allow.has(s.id));
  }, [visibleSourceIds]);

  const fileCount             = (id: string) => files[id]?.length ?? 0;
  // "Uploaded" per il submit ignora le card marcate `optional`.
  const requiredSources       = visibleSources.filter(s => !s.optional);
  const allRequiredUploaded   = requiredSources.every(s => fileCount(s.id) > 0);
  const uploadedSourcesCount  = visibleSources.filter(s => fileCount(s.id) > 0).length;
  // create: serve titolo + tutte le fonti obbligatorie. add-sources: almeno 1 file caricato.
  const canSubmit             = isAddMode
    ? uploadedSourcesCount > 0
    : titolo.trim().length > 0 && allRequiredUploaded;

  const addFiles = (id: string, multi: boolean, selected: File[]) => {
    setLoading(id);
    setTimeout(() => {
      setFiles(prev => {
        const existing = prev[id] ?? [];
        return { ...prev, [id]: multi ? [...existing, ...selected] : [selected[0]] };
      });
      setLoading(null);
    }, LOADING_DELAY_MS);
  };

  const removeFile = (id: string, idx: number) => {
    setFiles(prev => {
      const list = (prev[id] ?? []).filter((_, i) => i !== idx);
      const next = { ...prev };
      if (list.length === 0) delete next[id];
      else next[id] = list;
      return next;
    });
  };

  const handleClose = () => {
    setTitolo('');
    setAnno(currentYear);
    setFiles({});
    setLoading(null);
    onClose();
  };

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm({ titolo: titolo.trim(), anno, files });
    handleClose();
  };

  const errorMessage = isAddMode
    ? uploadedSourcesCount === 0 ? 'Carica almeno una fonte per procedere' : null
    : !allRequiredUploaded ? 'Carica almeno un file per ogni fonte obbligatoria'
      : titolo.trim().length === 0 ? 'Inserisci un titolo per procedere'
        : null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/40 z-40"
            onClick={handleClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col pointer-events-auto"
              style={{ maxHeight: 'calc(100vh - 64px)' }}
              onClick={e => e.stopPropagation()}
            >
              <Header isAddMode={isAddMode} onClose={handleClose} />

              <div className="overflow-y-auto px-6 py-5 flex-1 min-h-0">
                {!isAddMode && (
                  <Configurazione
                    titolo={titolo}
                    onTitoloChange={setTitolo}
                    anno={anno}
                    onAnnoChange={setAnno}
                  />
                )}

                <FontiDati
                  sources={visibleSources}
                  files={files}
                  loadingId={loading}
                  uploadedCount={uploadedSourcesCount}
                  onAdd={addFiles}
                  onRemove={removeFile}
                />
              </div>

              <Footer
                isAddMode={isAddMode}
                errorMessage={errorMessage}
                canSubmit={canSubmit}
                onClose={handleClose}
                onConfirm={handleConfirm}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreaReportModal;

/* ─── Sub-componenti locali ─────────────────────────────── */

const Header = ({ isAddMode, onClose }: { isAddMode: boolean; onClose: () => void }) => (
  <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
    <div className="flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#1B365D] flex items-center justify-center shrink-0">
        {isAddMode ? <Upload className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
      </div>
      <div>
        <h2 className="text-lg font-bold text-[#1B365D] leading-tight font-heading">
          {isAddMode ? 'Carica Fonti Mancanti' : 'Crea Nuovo Report'}
        </h2>
        <p className="text-xs text-gray-400 font-paragraph">
          {isAddMode ? 'Aggiungi le fonti ancora da collegare al report' : 'Configura il report e carica i file sorgente'}
        </p>
      </div>
    </div>
    <button
      onClick={onClose}
      className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
    >
      <X className="w-4 h-4" />
    </button>
  </div>
);

interface ConfigProps {
  titolo:         string;
  onTitoloChange: (v: string) => void;
  anno:           number;
  onAnnoChange:   (y: number) => void;
}

const Configurazione = ({ titolo, onTitoloChange, anno, onAnnoChange }: ConfigProps) => (
  <div className="mb-6">
    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
      Configurazione
    </p>
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-2">
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Titolo del report <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          value={titolo}
          onChange={e => onTitoloChange(e.target.value)}
          placeholder="es. Conduct Risk 2025"
          className="w-full border border-borderDivider rounded-lg px-3 py-2 text-sm text-textPrimary font-medium bg-white placeholder:font-normal placeholder:text-gray-300 hover:border-textPrimary/30 focus:outline-none focus:border-textPrimary/40 focus:ring-1 focus:ring-textPrimary/20 transition-all"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Anno di riferimento
        </label>
        <YearSelect value={anno} years={YEARS} onChange={onAnnoChange} />
      </div>
    </div>
  </div>
);

interface FontiProps {
  sources:       FileSource[];
  files:         Record<string, File[]>;
  loadingId:     string | null;
  uploadedCount: number;
  onAdd:         (id: string, multi: boolean, selected: File[]) => void;
  onRemove:      (id: string, idx: number) => void;
}

const FontiDati = ({ sources, files, loadingId, uploadedCount, onAdd, onRemove }: FontiProps) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
        Fonti dati
      </p>
      <span className="text-xs text-gray-400">
        {uploadedCount}/{sources.length} caricati
      </span>
    </div>

    <div className="space-y-2">
      {sources.map((src, i) => (
        <SourceCard
          key={src.id}
          source={src}
          files={files[src.id] ?? []}
          loading={loadingId === src.id}
          index={i}
          onAdd={(selected) => onAdd(src.id, !!src.multi, selected)}
          onRemove={(idx) => onRemove(src.id, idx)}
        />
      ))}
    </div>
  </div>
);

interface FooterProps {
  isAddMode:    boolean;
  errorMessage: string | null;
  canSubmit:    boolean;
  onClose:      () => void;
  onConfirm:    () => void;
}

const Footer = ({ isAddMode, errorMessage, canSubmit, onClose, onConfirm }: FooterProps) => (
  <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between shrink-0">
    <div className="flex items-center gap-2">
      {errorMessage && (
        <span className="text-xs text-red-500 font-medium">{errorMessage}</span>
      )}
    </div>
    <div className="flex items-center gap-2">
      <button
        onClick={onClose}
        className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 transition-colors"
      >
        Annulla
      </button>
      <button
        onClick={onConfirm}
        disabled={!canSubmit}
        className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ backgroundColor: canSubmit ? '#1B365D' : undefined }}
        onMouseEnter={e => { if (canSubmit) e.currentTarget.style.backgroundColor = '#142a4a'; }}
        onMouseLeave={e => { if (canSubmit) e.currentTarget.style.backgroundColor = '#1B365D'; }}
      >
        {isAddMode ? <Upload className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        {isAddMode ? 'Carica Fonti' : 'Crea Report'}
      </button>
    </div>
  </div>
);
